import { useMutation } from '@tanstack/react-query';
import { HStack, useToast, VStack, Box, Button, Text } from '@cad-arc';
import { CadSearchsaveResource, EventSearchForm } from '@mark43/cad-api';
import { useState } from 'react';
import { createApiErrorHandler, useApiClient } from '@m43/core';
import { EventSearchResultsAppliedFilters } from './event-search-results-applied-filters';
import { SortMenu } from '../search-windows/sort-menu';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { useEventSearchSortContext } from './event-search-sort-provider';

export function EventSearchResultsHeader({
    hasResults,
    eventSearchForm,
    savedSearchId,
    setSavedSearchId,
}: {
    hasResults: boolean;
    eventSearchForm: EventSearchForm;
    savedSearchId?: number;
    setSavedSearchId: (savedSearchId?: number) => void;
}) {
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const sort = useEventSearchSortContext();
    const apiClient = useApiClient();
    const toast = useToast();
    const saveSearchMutation = useMutation((eventSearchForm: EventSearchForm) => {
        return apiClient
            .request<CadSearchsaveResource.SaveEventSearch>({
                method: 'POST',
                url: 'cad_search/save/event',
                data: { eventSearchForm },
            })
            .then((resp) => {
                const eventSearchSaveResponse = resp.data.data;
                if (eventSearchSaveResponse) {
                    setSavedSearchId(eventSearchSaveResponse.savedEventSearchDto.savedSearchId);
                }
            })
            .catch(
                createApiErrorHandler(() => {
                    toast({ status: 'error', description: errorOccurredWhile('saving search') });
                })
            );
    });
    const unsaveSearchMutation = useMutation((savedSearchId: number) => {
        return apiClient
            .request<CadSearchsaveResource.DeleteSavedEventSearch>({
                method: 'POST',
                url: 'cad_search/save/event/delete',
                data: { savedSearchId },
            })
            .then(() => {
                setSavedSearchId(undefined);
            })
            .catch(
                createApiErrorHandler(() => {
                    toast({ status: 'error', description: errorOccurredWhile('unsaving search') });
                })
            );
    });
    return (
        <VStack w="100%">
            <VStack
                backgroundColor="surface.canvas"
                w="100%"
                align="left"
                padding="24px 56px 12px 56px"
            >
                <Text variant="headingMd">Event Search Results</Text>
                <HStack justifyContent="space-between">
                    <SortMenu
                        isDisabled={!hasResults}
                        sortBy={{
                            value: sort.sortByValue,
                            onChange: (value) => sort.setSortByValue(value),
                            options: sort.sortByOptions,
                        }}
                        direction={{
                            value: sort.directionValue,
                            onChange: (value) => sort.setDirectionValue(value),
                            options: sort.directionOptions,
                        }}
                    />
                    <Button
                        variant="ghost"
                        leadingVisual={showFilters ? 'Close' : 'Open'}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        {`${showFilters ? 'Hide' : 'Show'} Applied Filters`}
                    </Button>
                    {savedSearchId ? (
                        <Button
                            variant="ghost"
                            leadingVisual="SavedSearchActive"
                            onClick={() => unsaveSearchMutation.mutate(savedSearchId)}
                            isLoading={unsaveSearchMutation.isLoading}
                        >
                            Unsave Search
                        </Button>
                    ) : (
                        <Button
                            variant="ghost"
                            leadingVisual="SavedSearchInactive"
                            onClick={() => saveSearchMutation.mutate(eventSearchForm)}
                            isLoading={saveSearchMutation.isLoading}
                        >
                            Save Search
                        </Button>
                    )}
                </HStack>
            </VStack>
            {showFilters && <EventSearchResultsAppliedFilters eventSearchForm={eventSearchForm} />}
            {/* for spacing */}
            <Box />
        </VStack>
    );
}
